import { ReactNode } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-permission";
import { PermissionKey } from "@/types/permissions";
import { VariantProps } from "class-variance-authority";

interface ProtectedButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  permission: PermissionKey | PermissionKey[];
  requireAll?: boolean;
  hideIfNoPermission?: boolean;
  disableIfNoPermission?: boolean;
  noPermissionTooltip?: string;
  children: ReactNode;
  asChild?: boolean;
}

/**
 * Button component that respects user permissions
 * Can be hidden or disabled based on permissions
 */
export function ProtectedButton({
  permission,
  requireAll = false,
  hideIfNoPermission = false,
  disableIfNoPermission = true,
  noPermissionTooltip = "You don't have permission to perform this action",
  children,
  disabled,
  variant,
  size,
  asChild,
  ...props
}: ProtectedButtonProps) {
  const hasPermission = usePermission(permission, requireAll);

  // Hide button if no permission and hideIfNoPermission is true
  if (!hasPermission && hideIfNoPermission) {
    return null;
  }

  // If user has permission or we're not disabling, render normal button
  if (hasPermission || !disableIfNoPermission) {
    return (
      <Button disabled={disabled} variant={variant} size={size} asChild={asChild} {...props}>
        {children}
      </Button>
    );
  }

  // Render disabled button with tooltip
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button disabled variant={variant} size={size} asChild={asChild} {...props}>
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{noPermissionTooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
